import {
  GetStudyProgrammePageData,
  GetStudyProgrammePageDataVariables,
} from "__generated__/types";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalTabbedLayout } from "common/components/layouts/NormalTabbedLayout";
import { ContentElementList } from "common/components/lists/content/ContentElementList";
import { studyProgrammeElementListFragment } from "common/components/lists/content/fragments";
import { MultilineText } from "common/components/MultilineText";
import { NoDataError } from "common/components/NoDataError";
import { Section } from "common/components/Section";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import gql from "graphql-tag";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

type RouteProps = {
  studyProgrammeId: string;
};

const DATA_QUERY = gql`
  query GetStudyProgrammePageData($studyProgrammeId: ID!) {
    studyProgrammeById(id: $studyProgrammeId) {
      ...ContentBreadcrumbFragment
      ...StudyProgrammeElementListFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${studyProgrammeElementListFragment}
`;

export const StudyProgrammePage: React.FC = () => {
  const { studyProgrammeId } = useParams<RouteProps>();

  if (!studyProgrammeId) {
    throw new Error("Unexpected error: Missing studyProgrammeId");
  }

  const { loadingOrError, data } = useSimpleQuery<
    GetStudyProgrammePageData,
    GetStudyProgrammePageDataVariables
  >(DATA_QUERY, {
    variables: {
      studyProgrammeId,
    },
  });

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.studyProgrammeById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  return (
    <NormalTabbedLayout
      title={data.studyProgrammeById.title}
      context={data.studyProgrammeById.context ?? []}
      navigationTabs={[
        {
          title: "Innhold",
          path: urls.studyProgramme(studyProgrammeId),
          content: (
            <Section title="Pensum">
              <ContentElementList parent={data.studyProgrammeById} />
            </Section>
          ),
        },
        {
          title: "Om studieprogrammet",
          path: urls.studyProgrammeDescription(studyProgrammeId),
          content: (
            <>
              {data.studyProgrammeById.description ? (
                <MultilineText {...data.studyProgrammeById.description} />
              ) : (
                <p>Ingen beskrivelse er oppgitt.</p>
              )}
            </>
          ),
        },
      ]}
      footer={
        data.studyProgrammeById.canUpdateDetails.canPerform ? (
          <>
            <Link to={urls.studyProgrammeAdminEdit(data.studyProgrammeById.id)}>
              Rediger detaljer
            </Link>
            <br />
            <Link to={urls.studyProgrammeAdmin(data.studyProgrammeById.id)}>
              Rediger innhold
            </Link>
          </>
        ) : undefined
      }
    />
  );
};
